import { useState, useCallback } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

interface NavItem {
  label: string;
  path: string;
  icon: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: '\u25A6' },
  { label: 'Clients', path: '/clients', icon: '\u2B24' },
  { label: 'Invoices', path: '/invoices', icon: '\u2B1A' },
  { label: 'Leads', path: '/leads', icon: '\u2606' },
  { label: 'Bids', path: '/bids', icon: '\u2691' },
  { label: 'Templates', path: '/templates', icon: '\u2630' },
  { label: 'Notifications', path: '/notifications', icon: '\u2407' },
  { label: 'Settings', path: '/settings', icon: '\u2699' },
];

const layoutStyles = {
  wrapper: {
    display: 'flex',
    minHeight: '100vh',
  } as const,

  sidebar: {
    width: 'var(--sidebar-width)',
    background: 'var(--color-surface)',
    borderRight: '1px solid var(--color-border)',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 40,
    transition: 'transform 0.2s ease',
  } as const,

  sidebarHidden: {
    transform: 'translateX(-100%)',
  } as const,

  sidebarBrand: {
    padding: '16px 20px',
    borderBottom: '1px solid var(--color-border)',
    display: 'flex',
    alignItems: 'center',
  } as const,

  brandLogo: {
    height: '36px',
    width: 'auto',
  } as const,

  nav: {
    flex: 1,
    padding: '16px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    overflowY: 'auto',
  } as const,

  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 16px',
    borderRadius: 'var(--radius-md)',
    color: 'var(--color-text-muted)',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    transition: 'background 0.15s, color 0.15s',
  } as const,

  navLinkActive: {
    background: 'var(--color-primary)',
    color: '#fff',
  } as const,

  navIcon: {
    width: '20px',
    textAlign: 'center',
    fontSize: '1rem',
  } as const,

  sidebarFooter: {
    padding: '16px 24px',
    borderTop: '1px solid var(--color-border)',
    fontSize: '0.75rem',
    color: 'var(--color-text-muted)',
  } as const,

  mainArea: {
    flex: 1,
    marginLeft: 'var(--sidebar-width)',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  } as const,

  topbar: {
    height: 'var(--topbar-height)',
    background: 'var(--color-surface)',
    borderBottom: '1px solid var(--color-border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    position: 'sticky',
    top: 0,
    zIndex: 30,
  } as const,

  topbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  } as const,

  menuButton: {
    display: 'none',
    background: 'none',
    border: 'none',
    color: 'var(--color-text)',
    fontSize: '1.5rem',
    padding: '4px',
    cursor: 'pointer',
  } as const,

  topbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  } as const,

  userMenu: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  } as const,

  userButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 12px',
    background: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--color-text)',
    cursor: 'pointer',
    fontSize: '0.875rem',
  } as const,

  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'var(--color-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.75rem',
  } as const,

  dropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '8px',
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-lg)',
    minWidth: '200px',
    padding: '8px',
    zIndex: 50,
  } as const,

  dropdownItem: {
    display: 'block',
    width: '100%',
    padding: '8px 12px',
    background: 'none',
    border: 'none',
    color: 'var(--color-text)',
    fontSize: '0.875rem',
    textAlign: 'left' as const,
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
  } as const,

  dropdownDivider: {
    height: '1px',
    background: 'var(--color-border)',
    margin: '4px 0',
  } as const,

  content: {
    flex: 1,
    padding: '24px',
  } as const,

  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    zIndex: 35,
  } as const,
} as const;

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    logout();
    setUserMenuOpen(false);
  }, [logout]);

  const handleNavigateSettings = useCallback(() => {
    navigate('/settings');
    setUserMenuOpen(false);
  }, [navigate]);

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '??';

  return (
    <div style={layoutStyles.wrapper}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          style={layoutStyles.overlay}
          onClick={() => setSidebarOpen(false)}
          role="presentation"
        />
      )}

      {/* Sidebar */}
      <aside
        style={{
          ...layoutStyles.sidebar,
          ...(sidebarOpen ? {} : {}),
        }}
        data-sidebar
      >
        <div style={layoutStyles.sidebarBrand}>
          <img src="/rslogo.png" alt="Reel Suite Pro" style={layoutStyles.brandLogo} />
        </div>

        <nav style={layoutStyles.nav}>
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              onClick={() => setSidebarOpen(false)}
              style={({ isActive }) => ({
                ...layoutStyles.navLink,
                ...(isActive ? layoutStyles.navLinkActive : {}),
              })}
            >
              <span style={layoutStyles.navIcon}>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div style={layoutStyles.sidebarFooter}>
          {user?.subscriptionTier && (
            <span style={{ textTransform: 'capitalize' }}>
              {user.subscriptionTier} Plan
            </span>
          )}
        </div>
      </aside>

      {/* Main content area */}
      <div style={layoutStyles.mainArea}>
        {/* Top bar */}
        <header style={layoutStyles.topbar}>
          <div style={layoutStyles.topbarLeft}>
            <button
              style={layoutStyles.menuButton}
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle sidebar"
            >
              &#9776;
            </button>
          </div>

          <div style={layoutStyles.topbarRight}>
            <div style={layoutStyles.userMenu}>
              <button
                style={layoutStyles.userButton}
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                aria-label="User menu"
              >
                <div style={layoutStyles.avatar}>{initials}</div>
                <span>{user?.name ?? 'User'}</span>
              </button>

              {userMenuOpen && (
                <div style={layoutStyles.dropdown}>
                  <div style={{ padding: '8px 12px' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      {user?.name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                      {user?.email}
                    </div>
                  </div>
                  <div style={layoutStyles.dropdownDivider} />
                  <button
                    style={layoutStyles.dropdownItem}
                    onClick={handleNavigateSettings}
                  >
                    Settings
                  </button>
                  <button
                    style={layoutStyles.dropdownItem}
                    onClick={handleLogout}
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main style={layoutStyles.content}>
          <Outlet />
        </main>
      </div>

      {/* Responsive sidebar styles injected via a style tag */}
      <style>{`
        @media (max-width: 768px) {
          [data-sidebar] {
            transform: translateX(-100%);
          }
          [data-sidebar].open {
            transform: translateX(0);
          }
          div[style*="margin-left: var(--sidebar-width)"] {
            margin-left: 0 !important;
          }
          button[aria-label="Toggle sidebar"] {
            display: flex !important;
          }
        }
      `}</style>
    </div>
  );
}
